import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { nomenclature as nomenclatureApi, orgs } from '@/api/catalogs';
import {
  retailNomenclaturePrices,
  type NomenclatureRetailPrice,
} from '@/api/retailNomenclaturePrices';
import { retailPriceGroups } from '@/api/retailPriceGroups';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.nomenclature_prices.manage';

export default function NomenclatureRetailPricesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-nomenclature-prices', orgId],
    queryFn: () =>
      retailNomenclaturePrices.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomenclatureApi.listAll(),
  });
  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);

  const pgQ = useQuery({
    queryKey: ['retail-price-groups', orgId],
    queryFn: () => retailPriceGroups.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });
  const pgName = useMemo(() => {
    const m = new Map<string, string>();
    pgQ.data?.items.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [pgQ.data]);

  const cols = useMemo<ColumnDef<NomenclatureRetailPrice>[]>(
    () => [
      {
        key: 'nomenclature_id',
        header: 'Номенклатура',
        sortValue: (r) => nomName.get(r.nomenclature_id) ?? '',
        cell: (r) => nomName.get(r.nomenclature_id) ?? '',
      },
      {
        key: 'price_group_id',
        header: 'Цінова група',
        width: 180,
        sortValue: (r) => pgName.get(r.price_group_id) ?? '',
        cell: (r) => pgName.get(r.price_group_id) ?? '',
      },
      {
        key: 'price',
        header: 'Ціна',
        width: 130,
        align: 'right',
        sortValue: (r) => Number(r.price),
        cell: (r) =>
          r.price
            ? Number(r.price).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        key: 'vat_included',
        header: 'З ПДВ',
        width: 100,
        align: 'center',
        sortValue: (r) => (r.vat_included ? 1 : 0),
        cell: (r) => (r.vat_included ? '✓' : '—'),
      },
      {
        key: 'valid_from',
        header: 'Діє з',
        width: 160,
        sortValue: (r) => r.valid_from ?? '',
        cell: (r) =>
          r.valid_from ? new Date(r.valid_from).toLocaleDateString('uk-UA') : '',
      },
      {
        key: 'is_active',
        header: 'Активна',
        width: 110,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
    ],
    [nomName, pgName],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати роздрібними цінами (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Роздрібні ціни
          <HintBadge
            title="Що це?"
            text={
              'Роздрібні ціни — це прайс товарів у конкретній «ціновій групі». ' +
              'Один товар може мати РIЗНУ ціну у різних групах ' +
              '(наприклад «Роздріб» 100 грн, «Опт» 85 грн).\n\n' +
              'Як заповнити:\n' +
              '• Спочатку створіть «Цінові групи» (хоча б одну «Роздріб»).\n' +
              '• Тут додайте ціну для кожного товару, який буде продаватись на касі.\n' +
              '• Для масового заведення / зміни цін — користуйтесь документом «Зміна цін».\n\n' +
              'Якщо ціна відсутня — товар з\'явиться у POS з позначкою «— ціна —» і не продастся.'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/retail/nomenclature-retail-prices/new')}
        >
          + Нова ціна
        </button>
      </div>
      <DataTable<NomenclatureRetailPrice>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає цін."
        onRowDoubleClick={(r) => navigate(`/retail/nomenclature-retail-prices/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="nomenclature_id"
      />
    </>
  );
}
